import {
    buildDriverSteps,
    startDriverTour,
} from 'js/onboarding/driverTour';
import {
    TOUR_STATUS,
    buildScopedTourStorageKey,
    readTourState,
    writeTourState,
} from 'js/onboarding/tourState';

export const HOME_DASHBOARD_TOUR_ID = 'home';
export const HOME_DASHBOARD_TOUR_VERSION = 'v1';

const DEFAULT_HOME_DASHBOARD_TOUR_STATE = Object.freeze({
    status: TOUR_STATUS.NOT_STARTED,
});

export function getHomeDashboardTourStorageKey({ user = '' } = {}) {
    return buildScopedTourStorageKey({
        tourId: HOME_DASHBOARD_TOUR_ID,
        version: HOME_DASHBOARD_TOUR_VERSION,
        user,
    });
}

export function getHomeDashboardTourState(
    context = {},
    storage = window.localStorage
) {
    return readTourState(
        getHomeDashboardTourStorageKey(context),
        storage,
        DEFAULT_HOME_DASHBOARD_TOUR_STATE
    );
}

export function saveHomeDashboardTourState(
    context = {},
    nextState = {},
    storage = window.localStorage
) {
    return writeTourState(
        getHomeDashboardTourStorageKey(context),
        nextState,
        storage,
        DEFAULT_HOME_DASHBOARD_TOUR_STATE
    );
}

export function markHomeDashboardTourCompleted(
    context = {},
    storage = window.localStorage
) {
    return saveHomeDashboardTourState(
        context,
        { status: TOUR_STATUS.COMPLETED },
        storage
    );
}

export function markHomeDashboardTourDismissed(
    context = {},
    storage = window.localStorage
) {
    return saveHomeDashboardTourState(
        context,
        { status: TOUR_STATUS.DISMISSED },
        storage
    );
}

export function shouldStartHomeDashboardTour({
    isAuthenticated = false,
    state = DEFAULT_HOME_DASHBOARD_TOUR_STATE,
} = {}) {
    if (!isAuthenticated) {
        return false;
    }

    return ![
        TOUR_STATUS.COMPLETED,
        TOUR_STATUS.DISMISSED,
    ].includes(state?.status);
}

export function buildHomeDashboardTourSteps() {
    return [
        {
            title: 'Welcome to SmartData',
            content:
                'SmartData helps you upload, organise, publish, and review spatial datasets. You can reopen this tour later whenever you need a refresher.',
            side: 'over',
            align: 'center',
        },
        {
            title: 'Dashboard Overview',
            target: '[data-home-tour-dashboard]',
            content:
                'This dashboard brings together your recent activity, quick actions, and the main navigation paths you will use most often.',
        },
        {
            title: 'Teams / Workspaces',
            target: '[data-home-tour-teams]',
            content:
                'Teams organise people, assets, and permissions so the right users can collaborate inside the right workspace.',
        },
        {
            title: 'Upload New Asset',
            target: '[data-home-tour-upload-link]',
            content:
                'Start new uploads here when you want to add data to SmartData. BYO Hosting users can also upload preprocessed dataset packages through this path.',
        },
        {
            title: 'Billing & Account Status',
            target: '[data-home-tour-billing]',
            content:
                'Use billing to review subscriptions, plans, and account access so your workspace stays active and correctly assigned.',
        },
        {
            title: 'Viewer / Recent Datasets',
            target: '[data-home-tour-recent-datasets]',
            content:
                'Recent datasets and views help you jump back into published work quickly so you can review, share, and continue where you left off.',
        },
        {
            title: 'Help & Support',
            target: '[data-home-tour-help]',
            content:
                'Help, documentation, and support are always close by if you need onboarding guidance or technical assistance.',
        },
    ];
}

export async function startHomeDashboardTour({
    context = {},
    storage = window.localStorage,
    createDriver,
} = {}) {
    const state = getHomeDashboardTourState(context, storage);
    const isAuthenticated = Boolean(context?.user);

    if (!shouldStartHomeDashboardTour({ isAuthenticated, state })) {
        return { started: false, state };
    }

    const steps = await buildDriverSteps(buildHomeDashboardTourSteps());
    if (!steps.length) {
        return { started: false, state };
    }

    const tour = startDriverTour({
        steps,
        createDriver,
        onCompleted: () => {
            markHomeDashboardTourCompleted(context, storage);
        },
        onDismissed: () => {
            markHomeDashboardTourDismissed(context, storage);
        },
    });

    return {
        started: Boolean(tour),
        state,
        tour,
    };
}
